/**
 * @name useBillingAndUsageAccess
 * @description Custom hook to provide information about payments, usage
 * @returns
 */

import type { Environment } from '@flatfile/api-internal/api'
import { BillingLimitsResponse, BillingPlanWithUsageResponse } from '../../../api/types'
import { fromMaybe } from '../utilities/fromMaybe'
import { useEntitlements } from '../../../hooks/useEntitlements'

export type Plan = 'starter' | 'starter-payg' | 'pro' | 'pro-discount' | 'enterprise'
interface UsageBannerProps {
  filesUsageObject: {
    used: number
    limit: number
  }
  pdvUsagePercentage: number
  plan: Plan
  billingUrl: string
}

interface UseBillingAndUsageAccessResponse {
  fileUploadLocked: boolean | undefined
  sheetIsLocked: boolean | undefined
  usageBannerProps: UsageBannerProps
  filesUsage?: number
  pdvUsage?: number
  freeFileLimit: number
  freePdvLimit: number
}

interface UseBillingAndUsageAccessProps {
  limitsData?: BillingLimitsResponse
  planData?: BillingPlanWithUsageResponse
  environment?: Environment | undefined
  isGuest: boolean
}

// constants
export const FREE_FILE_LIMIT = 50
export const FREE_PDV_LIMIT = 10_000_000

// util methods
const getFilesUsageObject = (filesUsage: number, freeFileLimit: number) => ({
  used: filesUsage,
  limit: freeFileLimit,
})
const getPdvUsagePercentage = (pdvUsage: number, freePdvLimit: number) => (pdvUsage / freePdvLimit) * 100

const getPlan = (plan: BillingPlanWithUsageResponse) => plan.data?.plan

export const disabledLimitParams = {
  fileUploadLocked: false,
  sheetIsLocked: false,
  usageBannerProps: {
    filesUsageObject: { used: 0, limit: 0 },
    pdvUsagePercentage: 0,
    plan: 'pro-discount' as Plan,
    billingUrl: `/billing`,
  },
  freeFileLimit: FREE_FILE_LIMIT,
  freePdvLimit: FREE_PDV_LIMIT,
}

export const useBillingAndUsageAccess = ({
  limitsData,
  planData,
  environment,
  isGuest,
}: UseBillingAndUsageAccessProps): UseBillingAndUsageAccessResponse => {
  const { hasEntitlement } = useEntitlements()
  if (!limitsData || !planData) {
    // Return default response if data is still being fetched
    return {
      ...disabledLimitParams,
      fileUploadLocked: undefined,
      sheetIsLocked: undefined,
    }
  }

  // usage constants
  const filesUsage = fromMaybe(planData?.data?.files, 0)
  const pdvUsage = fromMaybe(planData?.data?.pdvs, 0)

  // check for feature flag
  const billingEnabled = hasEntitlement('billingEnabled') || false

  const isProduction = environment?.isProd || false
  const lockingConditions = !billingEnabled || !isProduction

  const fileUploadLocked = lockingConditions ? false : limitsData?.data?.fileUploadIsLockedUntilPayment
  const sheetIsLocked = lockingConditions ? false : limitsData?.data?.sheetIsLockedUntilPayment

  const plan = getPlan(planData!) as Plan

  const baseResponse = {
    fileUploadLocked,
    sheetIsLocked,
    filesUsage,
    pdvUsage,
    freeFileLimit: FREE_FILE_LIMIT,
    freePdvLimit: FREE_PDV_LIMIT,
    usageBannerProps: {
      filesUsageObject: getFilesUsageObject(filesUsage, FREE_FILE_LIMIT),
      pdvUsagePercentage: getPdvUsagePercentage(pdvUsage, FREE_PDV_LIMIT),
      plan,
      billingUrl: '/billing',
    },
  }

  if (isGuest) {
    return { ...disabledLimitParams, fileUploadLocked, sheetIsLocked }
  }

  return billingEnabled ? baseResponse : disabledLimitParams
}
